import React, { useState, useEffect } from 'react';
import { Plus, Minus, Split } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { subdivisionsApi } from '../../lib/api';
import { toast } from 'sonner';
import { formatCurrency } from '../../lib/utils';

// Two ways to allocate cost basis across child lots:
//   - 'acres'   → each lot gets its share of cost basis = lot.acres / total_acres
//   - 'percent' → each lot gets the % the user typed, summing to 100
const MODE_ACRES = 'acres';
const MODE_PERCENT = 'percent';

export const SubdivisionFormDialog = ({ open, onOpenChange, project, onSuccess }) => {
  const parentAcres = parseFloat(project?.acres) || 0;
  // Default mode = Acres (per user request); fall back to Percent if parent
  // doesn't have acreage set.
  const [mode, setMode] = useState(parentAcres > 0 ? MODE_ACRES : MODE_PERCENT);

  const initialLots = () => (
    parentAcres > 0
      ? [
          { name: '', acres: (parentAcres / 2).toFixed(2), allocation: 50 },
          { name: '', acres: (parentAcres / 2).toFixed(2), allocation: 50 },
        ]
      : [
          { name: '', acres: '', allocation: 50 },
          { name: '', acres: '', allocation: 50 },
        ]
  );

  const [childLots, setChildLots] = useState(initialLots);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setMode(parentAcres > 0 ? MODE_ACRES : MODE_PERCENT);
      setChildLots(initialLots());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // When switching modes, recompute the inactive field from the active one.
  const switchMode = (newMode) => {
    if (newMode === mode) return;
    if (newMode === MODE_ACRES) {
      setChildLots(prev => prev.map(lot => ({
        ...lot,
        acres: parentAcres > 0
          ? ((parseFloat(lot.allocation) || 0) / 100 * parentAcres).toFixed(2)
          : '',
      })));
    } else {
      const totalAcresEntered = childLots.reduce((s, l) => s + (parseFloat(l.acres) || 0), 0);
      setChildLots(prev => prev.map(lot => ({
        ...lot,
        allocation: totalAcresEntered > 0
          ? ((parseFloat(lot.acres) || 0) / totalAcresEntered * 100).toFixed(2)
          : ((100 / prev.length).toFixed(2)),
      })));
    }
    setMode(newMode);
  };

  // Derived totals
  const totalAcresEntered = childLots.reduce((s, l) => s + (parseFloat(l.acres) || 0), 0);
  const computedAllocations = mode === MODE_ACRES
    ? childLots.map(l => parentAcres > 0 ? (parseFloat(l.acres) || 0) / parentAcres * 100 : 0)
    : childLots.map(l => parseFloat(l.allocation) || 0);
  const totalAllocation = computedAllocations.reduce((s, v) => s + v, 0);
  const parentCostBasis = project?.purchase_price || 0;
  const acresMismatch = mode === MODE_ACRES && parentAcres > 0 && Math.abs(totalAcresEntered - parentAcres) > 0.01;

  const addLot = () => {
    if (mode === MODE_PERCENT) {
      const newAllocation = 100 / (childLots.length + 1);
      const updatedLots = childLots.map(lot => ({
        ...lot,
        allocation: newAllocation.toFixed(2),
      }));
      updatedLots.push({ name: '', acres: '', allocation: newAllocation.toFixed(2) });
      setChildLots(updatedLots);
    } else {
      const evenAcres = parentAcres > 0 ? parentAcres / (childLots.length + 1) : 0;
      const updatedLots = childLots.map(lot => ({
        ...lot,
        acres: evenAcres > 0 ? evenAcres.toFixed(2) : lot.acres,
      }));
      updatedLots.push({ name: '', acres: evenAcres > 0 ? evenAcres.toFixed(2) : '', allocation: 0 });
      setChildLots(updatedLots);
    }
  };

  const removeLot = (index) => {
    if (childLots.length <= 2) return;
    const newLots = childLots.filter((_, i) => i !== index);
    if (mode === MODE_PERCENT) {
      const newAllocation = 100 / newLots.length;
      setChildLots(newLots.map(lot => ({ ...lot, allocation: newAllocation.toFixed(2) })));
    } else {
      const evenAcres = parentAcres > 0 ? parentAcres / newLots.length : 0;
      setChildLots(newLots.map(lot => ({ ...lot, acres: evenAcres > 0 ? evenAcres.toFixed(2) : lot.acres })));
    }
  };

  const updateLot = (index, field, value) => {
    setChildLots(prev => prev.map((l, i) => i === index ? { ...l, [field]: value } : l));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const hasEmptyNames = childLots.some(lot => !lot.name.trim());
    if (hasEmptyNames) {
      toast.error('All lots must have a name');
      return;
    }
    if (mode === MODE_ACRES && parentAcres <= 0) {
      toast.error('Parent project has no acreage. Add acreage to the project or switch to Percent mode.');
      return;
    }
    if (mode === MODE_ACRES && acresMismatch) {
      toast.error(`Lot acres (${totalAcresEntered.toFixed(2)}) must sum to total acreage (${parentAcres.toFixed(2)})`);
      return;
    }
    if (mode === MODE_PERCENT && Math.abs(totalAllocation - 100) > 0.1) {
      toast.error('Allocations must sum to 100%');
      return;
    }

    setLoading(true);
    try {
      const allocationsPct = mode === MODE_ACRES
        ? childLots.map(lot => (parseFloat(lot.acres) || 0) / parentAcres * 100)
        : childLots.map(lot => parseFloat(lot.allocation));

      const response = await subdivisionsApi.create({
        parent_project_id: project.id,
        child_names: childLots.map(lot => lot.name.trim()),
        allocation_method: 'custom',
        custom_allocations: allocationsPct,
      });

      toast.success(`Created ${response.data.children.length} child lots`);
      onSuccess?.(response.data);
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to create subdivision:', error);
      toast.error(error.response?.data?.detail || 'Failed to create subdivision');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Split className="w-5 h-5" />
            Subdivide Project
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Parent Info */}
          <div className="p-4 bg-muted/30 rounded-sm">
            <p className="text-sm text-muted-foreground">Parent Project</p>
            <p className="font-medium">{project?.name}</p>
            <p className="text-sm font-mono mt-1">
              Cost Basis: {formatCurrency(parentCostBasis)}
              {parentAcres > 0 && (
                <span className="ml-3 text-muted-foreground">• {parentAcres.toFixed(2)} acres</span>
              )}
            </p>
          </div>

          {/* Mode Toggle */}
          <div className="flex items-center gap-3" data-testid="subdivision-mode-toggle">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Allocate by</Label>
            <div className="inline-flex rounded-sm border overflow-hidden">
              <button
                type="button"
                onClick={() => switchMode(MODE_ACRES)}
                disabled={parentAcres <= 0}
                className={`px-3 py-1.5 text-xs ${mode === MODE_ACRES ? 'bg-forest-500 text-white' : 'bg-white text-muted-foreground hover:bg-muted/50'} disabled:opacity-40 disabled:cursor-not-allowed`}
                data-testid="subdivision-mode-acres"
              >
                Acres
              </button>
              <button
                type="button"
                onClick={() => switchMode(MODE_PERCENT)}
                className={`px-3 py-1.5 text-xs ${mode === MODE_PERCENT ? 'bg-forest-500 text-white' : 'bg-white text-muted-foreground hover:bg-muted/50'}`}
                data-testid="subdivision-mode-percent"
              >
                Percent
              </button>
            </div>
            {parentAcres <= 0 && (
              <span className="text-xs text-amber-600">Parent has no acreage — Acres mode disabled.</span>
            )}
          </div>

          {/* Child Lots */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label>Child Lots</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addLot}
                className="rounded-sm"
              >
                <Plus className="w-4 h-4 mr-1" /> Add Lot
              </Button>
            </div>

            <div className="space-y-3 max-h-[300px] overflow-y-auto">
              {childLots.map((lot, index) => {
                const computedPct = computedAllocations[index] || 0;
                const computedCost = parentCostBasis * computedPct / 100;
                return (
                  <div key={index} className="flex items-center gap-3 p-3 border rounded-sm">
                    <div className="flex-1">
                      <Input
                        placeholder={`Lot ${index + 1} Name`}
                        value={lot.name}
                        onChange={(e) => updateLot(index, 'name', e.target.value)}
                        className="rounded-sm"
                        data-testid={`subdivision-lot-${index}-name`}
                      />
                    </div>
                    {mode === MODE_ACRES ? (
                      <div className="w-32">
                        <div className="flex items-center gap-1">
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={lot.acres}
                            onChange={(e) => updateLot(index, 'acres', e.target.value)}
                            className="rounded-sm text-right"
                            placeholder="0.00"
                            data-testid={`subdivision-lot-${index}-acres`}
                          />
                          <span className="text-sm text-muted-foreground">ac</span>
                        </div>
                      </div>
                    ) : (
                      <div className="w-32">
                        <div className="flex items-center gap-1">
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            max="100"
                            value={lot.allocation}
                            onChange={(e) => updateLot(index, 'allocation', e.target.value)}
                            className="rounded-sm text-right"
                            data-testid={`subdivision-lot-${index}-pct`}
                          />
                          <span className="text-sm text-muted-foreground">%</span>
                        </div>
                      </div>
                    )}
                    <div className="w-32 text-right">
                      <p className="text-[10px] text-muted-foreground">{computedPct.toFixed(2)}%</p>
                      <p className="text-sm font-mono">{formatCurrency(computedCost)}</p>
                    </div>
                    {childLots.length > 2 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeLot(index)}
                        className="text-red-600 hover:bg-red-50"
                      >
                        <Minus className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Totals */}
            <div className="flex items-center justify-between p-3 bg-muted/50 rounded-sm">
              <span className="font-medium">
                {mode === MODE_ACRES ? 'Total Acres' : 'Total Allocation'}
              </span>
              <span className={`font-mono font-bold ${
                (mode === MODE_ACRES ? acresMismatch : Math.abs(totalAllocation - 100) > 0.1)
                  ? 'text-red-600'
                  : 'text-green-600'
              }`}>
                {mode === MODE_ACRES
                  ? `${totalAcresEntered.toFixed(2)} / ${parentAcres.toFixed(2)} ac`
                  : `${totalAllocation.toFixed(2)}%`}
              </span>
            </div>
            {mode === MODE_PERCENT && Math.abs(totalAllocation - 100) > 0.1 && (
              <p className="text-sm text-red-600">
                Allocations must sum to 100%. Current total: {totalAllocation.toFixed(2)}%
              </p>
            )}
            {mode === MODE_ACRES && acresMismatch && (
              <p className="text-sm text-red-600">
                Lot acres must sum to {parentAcres.toFixed(2)} ac (currently {totalAcresEntered.toFixed(2)} ac).
              </p>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="rounded-sm"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                loading ||
                (mode === MODE_PERCENT && Math.abs(totalAllocation - 100) > 0.1) ||
                (mode === MODE_ACRES && acresMismatch)
              }
              className="bg-forest-500 hover:bg-forest-600 text-white rounded-sm"
              data-testid="subdivision-submit-btn"
            >
              {loading ? 'Creating...' : 'Create Subdivision'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
